import type { ReactNode } from 'react'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import type { LocalDate } from '../../platform/localDate'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { CashflowEmpty } from './CashflowEmpty'
import type { BarPick } from './charts/DivergingBars'
import type { CategoryMap } from './cashflowCategories'
import { periodWindow, priorPhrase, type CashflowView } from './cashflowPeriod'
import { deficitStarts } from './cashflowRules'
import type { LinkTarget } from './cashflowScope'
import type { Cashflow } from './cashflowWire'
import { IncomeExpensePanel } from './IncomeExpensePanel'
import { LockedChart, LockedWhereItWent, UpgradePrompt } from './LockedPreview'
import { MonthTable } from './MonthTable'
import { PeriodHeader } from './PeriodHeader'
import { PeriodTiles, PeriodTilesSkeleton } from './PeriodTiles'
import { UnconvertedNote } from './UnconvertedNote'
import type { CashflowResult } from './useCashflow'
import { WhereItCameFrom } from './WhereItCameFrom'
import { WhereItWent, type WentView } from './WhereItWent'
import './cashflow.css'

/** How the view changed: the header's arrows or toggle, or a chart pick (`BarPick`). */
export type ViewChange = 'prev' | 'next' | 'granularity' | BarPick

export type ScreenProps = {
  view: CashflowView
  /** The client's day; the period response's `window.today` replaces it once it arrives. */
  today: LocalDate
  /**
   * The earliest transaction date the container remembers from the last answered
   * period call. Used while the period is not answered (a failed step) so the
   * header's floor survives, free users included; null when there are none.
   */
  earliest?: LocalDate | null
  /** `useEntitlement('cashflow_details')`, read by the container. */
  entitled: boolean
  /**
   * The ten-year series behind the chart and the table. Null when there is no
   * chart region: a free user (who gets the locked chart instead), or an
   * entitled user with no transactions at all.
   */
  history: CashflowResult | null
  /** The selected period: totals, prior and, when entitled, the breakdown. */
  period: CashflowResult
  /** Category names by id (P4), built by the container; the screen fetches nothing. */
  categories: CategoryMap
  /** Every change to the view, and how it was made: the container writes the URL and emits. */
  onViewChange: (next: CashflowView, how: ViewChange) => void
  /** "All merchants →" was followed; the container emits `cashflow_merchant_expanded`. */
  onMerchantsExpanded: () => void
  onBreakdownViewChange?: (v: WentView) => void
  /** The detail path for a breakdown or income row, built by the container from the view. */
  rowLink: (t: LinkTarget) => string
}

/** Nothing moved at all in the period: no income, expense or transfer. */
function nothingMoved(d: Cashflow): boolean {
  const t = d.totals
  return t.incomeCents === 0 && t.expenseCents === 0 && t.transfersInCents === 0 && t.transfersOutCents === 0
}

// Success only: `cashflow` is a live surface, so `<Async>` throws on a stale state before this could read one.
const data = (s: AsyncState<Cashflow>): Cashflow | null => (s.status === 'success' ? s.data : null)

/** "Try again" calls the region's own retry, so one region's error never retries the other. */
function withRetry(s: AsyncState<Cashflow>, retry: () => void): AsyncState<Cashflow> {
  return s.status === 'error' ? { ...s, recovery: { label: 'Try again', onAct: retry } } : s
}

/** The chart's shape while its series is on the way. */
function ChartSkeleton() {
  return (
    <Panel className="cf-panel cf-chart-skeleton" aria-hidden="true">
      <Skeleton height={14} width="30%" />
      <Skeleton height={10} width="45%" />
      <Skeleton height={220} />
    </Panel>
  )
}

/** One list's shape: a title, a subtitle, a few rows. */
function ListSkeleton() {
  return (
    <Panel className="cf-panel" aria-hidden="true">
      <Skeleton height={14} width="30%" />
      <Skeleton height={10} width="20%" />
      <Skeleton height={120} />
    </Panel>
  )
}

function PeriodSkeleton({ lists }: { lists: boolean }) {
  return (
    <div className="cf-stack">
      <PeriodTilesSkeleton />
      {lists && <ListSkeleton />}
    </div>
  )
}

/**
 * The lists' area: one column, or at desktop and wide two — "Where it went"
 * on the left, "Where it came from" over Runway on the right. The column count
 * is a class from `useBreakpoint()` (rule 5), never a media query; the DOM
 * order is the one-column reading order either way.
 */
function Split({ two, children }: { two: boolean; children: ReactNode }) {
  return <div className={two ? 'cf-split cf-split--two' : 'cf-split'}>{children}</div>
}

/**
 * Entitled, but the body carries no breakdown: a free response still cached
 * across an upgrade, or an entitlement that resolved after the fetch (gate
 * N1). The detailed request is on its way, so the lists stand as skeletons —
 * never a blank, and never a lock or an Upgrade to a user who already has
 * premium. The skeletons are `aria-hidden`, so one line says what is happening.
 */
function DetailsPending({ two }: { two: boolean }) {
  return (
    <>
      <p className="sr-only">Loading cash-flow detail</p>
      <Split two={two}>
        <ListSkeleton />
        <ListSkeleton />
      </Split>
    </>
  )
}

/**
 * `/cashflow`'s presentational screen: ONE selected period drives the page.
 * It fetches nothing; the container hands it the two calls' results.
 *
 * Order: the period header, then the chart (premium) or its locked picture
 * (free), the tiles, then for premium "Where it went", "Where it came from" and
 * the table behind "All periods"; for free the one upgrade prompt and the
 * locked "Where it went". At desktop and wide the two lists sit side by side
 * (`Split`); below that the order is the same, in one column.
 *
 * The header sits outside both `<Async>` regions, so its controls stay put and
 * enabled through loading and error. The chart region wraps the history call
 * and the period region the period call; an error in one never hides the
 * other, and each one's Try again retries only its own call.
 *
 * Two empty states: no accounts replaces everything below the header; nothing
 * in the period replaces the tiles and lists, while the chart stays so another
 * bar is one click away. For a free user nothing-moved also drops the upgrade
 * prompt and both locked previews; the header's arrows are the way out.
 */
export function CashflowScreen({
  view,
  today,
  earliest: knownEarliest,
  entitled,
  history,
  period,
  categories,
  onViewChange,
  onMerchantsExpanded,
  onBreakdownViewChange,
  rowLink,
}: ScreenProps) {
  const bp = useBreakpoint()
  const two = bp === 'desktop' || bp === 'wide'
  const p = data(period.state)
  const h = history === null ? null : data(history.state)
  // One "today" (gate N7): the day the server cut the period at, once it has said.
  const asOf = p?.window.today ?? today
  const earliest = p !== null ? p.window.earliest : (knownEarliest ?? h?.window.earliest ?? null)
  const { partial } = periodWindow(view.period, view.granularity, asOf)
  const header = (
    <PeriodHeader
      view={view}
      today={asOf}
      earliest={earliest}
      partial={partial}
      shared={p?.scope.shared ?? 0}
      onChange={onViewChange}
    />
  )

  if (p !== null && p.scope.accounts === 0) {
    return (
      <div className="cf-page">
        {header}
        <CashflowEmpty kind="no-accounts" />
      </div>
    )
  }

  const busy = period.fetching && period.state.status === 'success'
  // A free user on an answered period where nothing moved gets the empty and no
  // upsell (m3-shape-empty-states.md: selling the detail of an empty period is
  // noise). The decision follows the data on screen: before the first answer
  // the previews hold their place, and during a step it is the PREVIOUS
  // period's (placeholderData keeps it as success) until the next one answers.
  // Deciding from the period in flight instead would only move the late shift.
  const upsell = !entitled && !(p !== null && nothingMoved(p))

  return (
    <div className="cf-page">
      {header}
      {upsell && <LockedChart granularity={view.granularity} />}
      {history !== null && (
        <Async
          surface="cashflow"
          state={withRetry(history.state, history.retry)}
          skeleton={<ChartSkeleton />}
          empty={{ title: 'No history yet' }}
        >
          {(d) => (
            <IncomeExpensePanel
              periods={d.periods ?? []}
              granularity={d.granularity}
              currency={d.currency}
              selected={view.period}
              onSelect={(start, via) => onViewChange({ ...view, period: start }, via)}
              deficits={deficitStarts(d.deficitPeriods)}
              deficitCount={d.deficitCount}
              completeCount={d.completeCount}
              earliest={d.window.earliest}
            />
          )}
        </Async>
      )}
      <div className="cf-body cf-period-body" aria-busy={busy ? 'true' : undefined}>
        <Async
          surface="cashflow"
          state={withRetry(period.state, period.retry)}
          skeleton={<PeriodSkeleton lists={entitled} />}
          // The screen decides emptiness from the body, so a container never sends
          // `status: 'empty'`. If one does, the chart is still the way out.
          empty={{ title: 'Nothing moved in this period' }}
        >
          {(d) => {
            if (nothingMoved(d)) {
              return (
                <div className="cf-stack">
                  <UnconvertedNote unconverted={d.unconverted} />
                  <CashflowEmpty kind="nothing-in-period" period={view.period} granularity={view.granularity} />
                </div>
              )
            }
            const phrase = d.prior === null ? null : priorPhrase(d.prior, d.granularity)
            return (
              <div className="cf-stack">
                <UnconvertedNote unconverted={d.unconverted} />
                <PeriodTiles data={d} />
                {entitled && d.breakdown === null && <DetailsPending two={two} />}
                {entitled && d.breakdown !== null && (
                  <Split two={two}>
                    <WhereItWent
                      breakdown={d.breakdown}
                      names={categories}
                      currency={d.currency}
                      expenseCents={d.totals.expenseCents}
                      priorPhrase={phrase}
                      onMerchantsExpanded={onMerchantsExpanded}
                      onViewChange={onBreakdownViewChange}
                      rowLink={rowLink}
                    />
                    <WhereItCameFrom
                      breakdown={d.breakdown}
                      incomeCents={d.totals.incomeCents}
                      names={categories}
                      currency={d.currency}
                      today={d.window.today}
                      rowLink={rowLink}
                    />
                  </Split>
                )}
              </div>
            )
          }}
        </Async>
      </div>
      {h !== null && h.periods !== null && h.periods.length > 0 && (
        <MonthTable
          periods={h.periods}
          granularity={h.granularity}
          currency={h.currency}
          categories={categories}
          deficits={deficitStarts(h.deficitPeriods)}
        />
      )}
      {upsell && (
        <>
          <UpgradePrompt />
          <LockedWhereItWent />
        </>
      )}
    </div>
  )
}
